import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { lastValueFrom, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { PatrolCommunitySourceId } from '../models/patrol-community-source.model';

export interface PatrolPlatformFlags {
  discord: boolean;
  roblox: boolean;
}

@Injectable({ providedIn: 'root' })
export class PatrolPlatformFlagsService {
  #http = inject(HttpClient);
  #flags = signal<PatrolPlatformFlags>({ discord: false, roblox: false });
  #ready = signal(false);
  #loadPromise: Promise<void> | null = null;

  readonly flags = this.#flags.asReadonly();

  isGloballyEnabled(id: PatrolCommunitySourceId): boolean {
    const flags = this.#flags();
    if (id === 'discord') {
      return flags.discord;
    }
    if (id === 'roblox') {
      return flags.roblox;
    }
    return false;
  }

  async whenReady(): Promise<void> {
    if (this.#ready()) {
      return;
    }
    if (!this.#loadPromise) {
      this.#loadPromise = this.#load();
    }
    await this.#loadPromise;
  }

  async reload(): Promise<void> {
    this.#ready.set(false);
    this.#loadPromise = this.#load();
    await this.#loadPromise;
  }

  async #load(): Promise<void> {
    try {
      const flags = await lastValueFrom(
        this.#http
          .get<PatrolPlatformFlags>(
            `${environment.baseUrl}/bots/patrol/platform-flags`,
          )
          .pipe(take(1)),
      );
      this.#flags.set(flags);
    } catch {
      this.#flags.set({ discord: false, roblox: false });
    } finally {
      this.#ready.set(true);
    }
  }
}
